import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, Clipboard, Platform } from 'react-native';
import { Button, Card, Text, TextInput, ActivityIndicator, Divider, Snackbar, Portal } from 'react-native-paper';
import { winthorApiService } from '../../services/winthorApi';
import { supabase } from '../../services/supabase';
import { extrairCodigoCliente } from '../../utils/chatwootUtils';

interface TestApiSectionProps {
  cliente?: any;
  chatwootData?: any;
}

const TestApiSection: React.FC<TestApiSectionProps> = ({ cliente, chatwootData }) => {
  const [codCliente, setCodCliente] = useState(() => {
    // Tentar extrair o código do cliente dos dados do Chatwoot, se disponíveis
    if (chatwootData) {
      const codCli = extrairCodigoCliente(chatwootData);
      if (codCli) return codCli.toString();
    }
    // Caso contrário, usar o código do cliente passado como prop
    return cliente?.codigo || '';
  });
  const [endpoint, setEndpoint] = useState('dash_titulos');
  const [resultado, setResultado] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [urlCompleta, setUrlCompleta] = useState<string>('');
  const [snackbar, setSnackbar] = useState({
    visible: false,
    mensagem: '',
    tipo: 'info' as 'info' | 'success' | 'error'
  });

  // Função para obter a rota da empresa a partir do ID
  const obterRotaEmpresa = async (empresaId: string): Promise<string> => {
    try {
      console.log('Buscando rota para a empresa:', empresaId);
      
      const { data: empresaData, error: empresaError } = await supabase
        .from('empresa')
        .select('rota')
        .eq('id', empresaId)
        .single();

      if (empresaError) {
        console.error('Erro ao buscar rota da empresa:', empresaError);
        throw new Error(`Erro ao buscar rota da empresa: ${empresaError.message}`);
      }
      
      if (!empresaData?.rota) {
        console.error('Rota da API não encontrada para a empresa:', empresaId);
        throw new Error(`Rota da API não encontrada para a empresa ${empresaId}`);
      }
      
      console.log('Rota encontrada:', empresaData.rota);
      return empresaData.rota;
    } catch (error) {
      console.error('Erro ao obter rota da empresa:', error);
      throw error;
    }
  };

  // Processar dados do Chatwoot
  const processarDadosChatwoot = () => {
    if (!chatwootData) {
      setErro('Dados do Chatwoot não disponíveis');
      setSnackbar({
        visible: true,
        mensagem: 'Dados do Chatwoot não disponíveis',
        tipo: 'error'
      });
      return;
    }
    
    console.log('Processando dados do Chatwoot:', JSON.stringify(chatwootData).substring(0, 200) + '...');
    
    // Verificar se os dados estão no formato do evento appContext
    const dadosParaExtracao = chatwootData.event === 'appContext' ? chatwootData : chatwootData;
    
    const codCli = extrairCodigoCliente(dadosParaExtracao);
    console.log('Resultado da extração do código do cliente:', codCli);
    
    if (codCli) {
      setCodCliente(codCli.toString());
      setSnackbar({
        visible: true,
        mensagem: `Código do cliente ${codCli} extraído com sucesso`,
        tipo: 'success'
      });
    } else {
      setErro('Não foi possível extrair o código do cliente dos dados do Chatwoot');
      setSnackbar({
        visible: true,
        mensagem: 'Código do cliente não encontrado nos dados do Chatwoot',
        tipo: 'error'
      });
    }
  };

  const testarApi = async (usarUrlDireta = false) => {
    if (!codCliente) {
      setErro('Informe o código do cliente');
      return;
    }

    try {
      setLoading(true);
      setErro(null);
      setResultado(null);
      setUrlCompleta('');

      console.log(`Testando API: ${endpoint} para cliente ${codCliente}`);
      
      let response;
      
      if (usarUrlDireta) {
        // Teste direto com parâmetro na URL
        console.log('Testando com parâmetro na URL');
        const empresaId = await winthorApiService.obterEmpresaUsuario();
        const rotaEmpresa = await obterRotaEmpresa(empresaId);
        
        const url = `https://worker-proxy.smartbr.workers.dev/sql/${rotaEmpresa}/${endpoint}?codcli=${codCliente}`;
        console.log('URL completa:', url);
        setUrlCompleta(url);
        
        // Obter o token de acesso do Supabase
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        
        if (!token) {
          throw new Error('Usuário não autenticado. Faça login novamente.');
        }
        
        console.log('Token obtido com sucesso');
        
        // Usar fetch para fazer a requisição direta
        const fetchResponse = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include',
        });
        
        if (!fetchResponse.ok) {
          throw new Error(`Erro na requisição: ${fetchResponse.status} ${fetchResponse.statusText}`);
        }
        
        response = await fetchResponse.json();
      } else {
        // Método normal usando o serviço
        response = await winthorApiService.executarConsulta(
          endpoint,
          { codcli: codCliente }
        );
      }

      console.log('Resposta bruta da API:', response);
      
      // Processa a resposta para extrair os dados
      let dados = response;
      
      // Se a resposta for um objeto com propriedade data que é um array
      if (response && typeof response === 'object' && 'data' in response && Array.isArray(response.data)) {
        console.log('Extraindo array de dados da resposta no componente');
        dados = response.data;
      }
      
      console.log('Dados processados:', dados);
      setResultado(dados);
      
      if (Array.isArray(dados) && dados.length === 0) {
        setErro('Nenhum registro encontrado para este cliente.');
      }
    } catch (error) {
      console.error('Erro ao testar API:', error);
      setErro(`Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  };

  // Função para renderizar os primeiros 10 registros do resultado
  const renderizarResultados = () => {
    if (!resultado || !Array.isArray(resultado) || resultado.length === 0) {
      return null;
    }

    // Limita a exibição aos primeiros 10 registros
    const registrosParaExibir = resultado.slice(0, 10);
    
    return (
      <ScrollView style={styles.scrollView}>
        <Text style={styles.resultadoTitle}>
          Encontrados {resultado.length} registros. Exibindo os primeiros 10:
        </Text>
        
        {registrosParaExibir.map((item, index) => (
          <View key={index} style={styles.itemContainer}>
            <Text style={styles.itemTitle}>Registro {index + 1}</Text>
            <Divider style={styles.divider} />
            {Object.entries(item).map(([key, value]) => (
              <Text key={key} style={styles.itemProperty}>
                <Text style={styles.propertyName}>{key}:</Text> {String(value)}
              </Text>
            ))}
          </View>
        ))}
      </ScrollView>
    );
  };

  return (
    <Card style={styles.container}>
      <Card.Title title="Teste da API" />
      <Card.Content>
        {chatwootData && (
          <Button
            mode="contained"
            onPress={processarDadosChatwoot}
            style={styles.chatwootButton}
            icon="account-search"
          >
            Extrair Código do Chatwoot
          </Button>
        )}
        
        <TextInput
          label="Código do Cliente"
          value={codCliente}
          onChangeText={setCodCliente}
          keyboardType="numeric"
          style={styles.input}
        />
        
        <TextInput
          label="Endpoint"
          value={endpoint}
          onChangeText={setEndpoint}
          style={styles.input}
        />
        
        <View style={styles.buttonContainer}>
          <Button 
            mode="contained" 
            onPress={() => testarApi(false)}
            loading={loading}
            disabled={loading}
            style={styles.button}
          >
            Testar API (Normal)
          </Button>
          
          <Button 
            mode="contained" 
            onPress={() => testarApi(true)}
            loading={loading}
            disabled={loading}
            style={[styles.button, styles.secondaryButton]}
          >
            Testar API (URL)
          </Button>
        </View>

        {urlCompleta ? (
          <View style={styles.urlContainer}>
            <Text style={styles.urlTitle}>URL da requisição:</Text>
            <Text selectable style={styles.urlText}>{urlCompleta}</Text>
            <Button 
              mode="outlined" 
              onPress={() => {
                Clipboard.setString(urlCompleta);
                setSnackbar({
                  visible: true,
                  mensagem: 'URL copiada para a área de transferência!',
                  tipo: 'success'
                });
              }}
              style={styles.copyButton}
            >
              Copiar URL
            </Button>
          </View>
        ) : null}
        
        <View style={styles.resultadoContainer}>
          {loading ? (
            <ActivityIndicator size="large" color="#0000ff" />
          ) : erro ? (
            <Text style={styles.errorText}>{erro}</Text>
          ) : resultado ? (
            renderizarResultados()
          ) : null}
        </View>
      </Card.Content>
      
      <Portal>
        <Snackbar
          visible={snackbar.visible}
          onDismiss={() => setSnackbar(prev => ({ ...prev, visible: false }))}
          duration={3000}
          style={{
            backgroundColor: 
              snackbar.tipo === 'success' ? '#4CAF50' : 
              snackbar.tipo === 'error' ? '#F44336' :
              '#2196F3'
          }}
        >
          {snackbar.mensagem}
        </Snackbar>
      </Portal>
    </Card>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 10,
  },
  chatwootButton: {
    marginBottom: 16,
    backgroundColor: '#4CAF50',
  },
  input: {
    marginBottom: 10,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
    marginBottom: 10,
  },
  button: {
    flex: 1,
    marginHorizontal: 5,
  },
  secondaryButton: {
    backgroundColor: '#4CAF50',
  },
  urlContainer: {
    marginTop: 15,
    padding: 10,
    backgroundColor: '#e3f2fd',
    borderRadius: 5,
  },
  urlTitle: {
    fontWeight: 'bold',
    marginBottom: 5,
    fontSize: 14,
  },
  urlText: {
    fontSize: 12,
    fontFamily: 'monospace',
    marginBottom: 10,
  },
  copyButton: {
    marginTop: 5,
  },
  resultadoContainer: {
    marginTop: 20,
    padding: 10,
    backgroundColor: '#f5f5f5',
    borderRadius: 5,
    minHeight: 50,
  },
  errorText: {
    color: '#d32f2f',
  },
  scrollView: {
    maxHeight: 400,
  },
  resultadoTitle: {
    fontWeight: 'bold',
    marginBottom: 10,
    fontSize: 14,
  },
  itemContainer: {
    marginBottom: 15,
    padding: 10,
    backgroundColor: '#ffffff',
    borderRadius: 5,
    elevation: 1,
  },
  itemTitle: {
    fontWeight: 'bold',
    marginBottom: 5,
    fontSize: 14,
    color: '#1976d2',
  },
  divider: {
    marginBottom: 5,
  },
  itemProperty: {
    marginVertical: 2,
    fontSize: 12,
  },
  propertyName: {
    fontWeight: 'bold',
    fontSize: 12,
  },
});

export default TestApiSection;
